import { useEffect, useRef, useState, type ReactNode } from 'react';
import { iconePara } from '../assets/items';
import { ehArmaCombate } from '../data/armas';
import { ehConsumivel } from '../data/consumiveis';
import { acaoDoSlot, SLOT_ARMA, type EsperaKind } from '../data/hotbar';
import { ehMagia, iconeSeguro, MAGIAS } from '../data/magias';
import { idiomaAtual, numero, t } from '../i18n/idioma';
import { nomeDaMagia, nomeDoItem } from '../i18n/nomes';
import { rotuloCurto, rotuloTecla, teclaDe } from '../input/atalhos';
import { useAtalhos } from '../input/useAtalhos';
import { useSkillsMagia } from '../world/skillsMagia';
import { useDica } from './hud/Dica';
import { DicaMagia } from './hud/dicasConteudo';
import { useIconeTingido } from './useIconeTingido';

interface SkillSlotProps {
  index: number;
  spellId: string | null;
  selected: boolean;
  war?: boolean;
  disabled: boolean;
  esperaAte: number | null;
  esperaMs: number;
  esperaTipo: EsperaKind | null;
  qty?: number;
  /** Sem pilha, o VIP mostra ∞ e paga cada uso com a prata (mochila e depois banco). */
  vip?: boolean;
  semBanco?: boolean;
  preco?: number;
  /** A prata que o VIP tem agora (mochila + banco). */
  prata?: number;
  /** `null` = personagem ainda não chegou; nenhum slot apaga. */
  manaAtual: number | null;
  onOpen(): void;
  onClear(): void;
  onUse(): void;
  arrastando?: boolean;
  /** A pergunta do botão direito ("tirar da barra?"), desenhada em cima do slot. */
  confirmarLimpar?: ReactNode;
  /** O bônus de velocidade de magia, para o tempo na caixa de dica. */
  velMagia?: number;
}

function esperaSegundos(ms: number): string {
  return (Math.max(0, ms) / 1000).toLocaleString(idiomaAtual(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
}

export function SkillSlot({
  index,
  spellId,
  selected,
  war,
  disabled,
  esperaAte,
  esperaMs,
  esperaTipo,
  qty,
  vip,
  semBanco,
  preco,
  prata,
  manaAtual,
  onOpen,
  onClear,
  onUse,
  arrastando,
  novo,
  confirmarLimpar,
  velMagia = 0
}: SkillSlotProps & { novo?: boolean }) {
  useAtalhos();
  const skills = useSkillsMagia();
  const acao = acaoDoSlot(index);
  const key = rotuloTecla(teclaDe(acao));
  const keyCurta = rotuloCurto(teclaDe(acao));
  const magia = spellId && ehMagia(spellId) ? MAGIAS[spellId] : null;
  const arma = spellId && ehArmaCombate(spellId) ? spellId : null;
  const item = spellId && ehConsumivel(spellId) ? spellId : null;
  const armaSlot = index === SLOT_ARMA;
  const occupied = spellId !== null;
  const infinita = !!vip && item != null && qty != null && qty <= 0;
  const acabou = item != null && qty != null && qty <= 0 && !infinita;
  const semMana = magia != null && manaAtual != null && magia.mana > manaAtual;
  // Magia no slot: a caixa de dica no lugar do title (techspec-tooltips-e-efeitos). Só mouse e teclado: o toque longo
  // do slot já abre a escolha.
  const dica = useDica(magia ? () => (
    <DicaMagia id={magia.id} magery={skills.magery} evalInt={skills.evalInt} velMagia={velMagia} nota={t('dk.tecla', { tecla: keyCurta })} />
  ) : null);
  /* a espera depois de usar: a sombra gira até sumir (CSS); o título conta os segundos ao passar o mouse */
  const [agora, setAgora] = useState(() => performance.now());
  useEffect(() => {
    if (esperaAte == null) return;
    setAgora(performance.now());
    const id = window.setTimeout(() => setAgora(performance.now()), Math.max(0, esperaAte - performance.now()) + 20);
    return () => window.clearTimeout(id);
  }, [esperaAte]);
  // Recarga de magia (techspec-combate-v2): o número dos segundos no slot anda a cada quarto de segundo.
  useEffect(() => {
    if (esperaAte == null || esperaTipo !== 'recarga') return;
    const id = window.setInterval(() => setAgora(performance.now()), 250);
    return () => window.clearInterval(id);
  }, [esperaAte, esperaTipo]);
  const remaining = esperaAte == null ? 0 : Math.max(0, esperaAte - agora);
  const esperando = esperaMs > 0 && remaining > 0;
  const s = esperaSegundos(remaining);
  const esperaTitle = esperando && esperaTipo === 'conjuracao' && magia
    ? t('magiaUi.espera.conjuracao', { magia: nomeDaMagia(magia.id), s })
    : esperando && esperaTipo === 'golpe' && arma
      ? t('magiaUi.espera.golpe', { arma: nomeDoItem(arma), s })
      : esperando && esperaTipo === 'uso' && item
        ? t('magiaUi.espera.uso', { item: nomeDoItem(item), s })
        : esperando && esperaTipo === 'recarga' && magia
          ? t('magiaUi.espera.recarga', { magia: nomeDaMagia(magia.id), s })
          : null;
  // Sem reserva: falta prata quando o servidor recusou ou quando a prata de agora não paga uma poção.
  const semPrata = !!semBanco || (prata ?? 0) < (preco ?? 0);
  const title = esperaTitle
    ?? (magia
      ? `${t('magiaUi.magiaEPagina', { magia: nomeDaMagia(magia.id), pagina: magia.pagina })} · ${t('magiaUi.custoMana', { mana: magia.mana })}`
      : arma
        ? nomeDoItem(arma)
          : item
          ? (infinita
            ? (semPrata
              ? t('vip.tooltipVazio')
              : t('vip.tooltip', { preco: numero(preco ?? 0), prata: numero(prata ?? 0) }))
            : qty == null ? nomeDoItem(item) : t('magiaUi.itemEQtd', { item: nomeDoItem(item), qtd: numero(qty) }))
          : occupied
            ? t('magiaUi.magiaDesconhecida')
            : armaSlot
              ? t('magiaUi.nenhumaArma')
              : t('magiaUi.slotVazio'));
  const label = magia
    ? t('magiaUi.slotComMagia', { tecla: key, magia: nomeDaMagia(magia.id), mana: magia.mana })
    : arma
      ? t('magiaUi.slotComArma', { tecla: key, arma: nomeDoItem(arma) })
      : item
        ? (qty == null
          ? t('magiaUi.slotComArma', { tecla: key, arma: nomeDoItem(item) })
          : t('magiaUi.slotComItem', { tecla: key, item: nomeDoItem(item), qtd: numero(qty) }))
        : occupied
          ? t('magiaUi.slotDesconhecido', { tecla: key })
          : t('magiaUi.slotVazioAria', { tecla: key });
  const iconeArma = useIconeTingido(arma);
  const icon = magia
    ? iconeSeguro(magia.id)
    : arma
      ? iconeArma
      : item
        ? iconePara(item, qty)
        : spellId
          ? iconeSeguro(spellId)
          : '';

  /* toque longo (celular) abre a escolha; o clique que vem depois não usa */
  const longo = useRef<{ t: number; usado: boolean }>({ t: 0, usado: false });
  const soltarLongo = () => window.clearTimeout(longo.current.t);
  useEffect(() => () => window.clearTimeout(longo.current.t), []);

  return (
    <>
    {confirmarLimpar}
    <button
      type="button"
      data-slot={index}
      onKeyDown={e => { if (e.repeat) e.preventDefault(); }}
      className={`bb-s${armaSlot ? ' bb-s--arma' : ''}${occupied || armaSlot ? '' : ' is-vazio'}${selected ? ' is-on' : ''}${war ? ' is-war' : ''}${acabou || semMana ? ' is-fraca' : ''}${arrastando ? ' is-arrastando' : ''}${novo ? ' is-novo' : ''}`}
      disabled={disabled}
      title={magia ? undefined : `${keyCurta} · ${title}`}
      onPointerEnter={dica.onPointerEnter}
      onFocus={dica.onFocus}
      onBlur={dica.onBlur}
      aria-label={label}
      aria-keyshortcuts={teclaDe(acao)}
      aria-pressed={selected}
      onPointerDown={e => {
        longo.current.usado = false;
        if (e.pointerType !== 'touch' || armaSlot) return;
        soltarLongo();
        longo.current.t = window.setTimeout(() => { longo.current.usado = true; onOpen(); }, 500);
      }}
      onPointerUp={soltarLongo}
      onPointerLeave={e => { soltarLongo(); dica.onPointerLeave(e); }}
      onPointerCancel={soltarLongo}
      onClick={() => {
        if (longo.current.usado) { longo.current.usado = false; return; }
        /* HUD V3: clique usa (como a tecla); slot vazio abre a escolha */
        if (occupied || armaSlot) onUse();
        else onOpen();
      }}
      onContextMenu={e => {
        e.preventDefault();
        /* o toque longo do celular não limpa: vira a escolha */
        if (longo.current.usado) return;
        if (occupied && !disabled && !armaSlot) onClear();
      }}
    >
      {occupied
        ? <img src={icon} alt="" width={32} height={32} draggable={false} />
        : armaSlot
          // Sem arma: dois punhos cruzados no traço dourado (protótipo corrida-e-sem-arma, opção B).
          ? <IcPunhos />
          : null}
      <kbd title={keyCurta !== key ? key : undefined}>{keyCurta}</kbd>
      {item && qty != null && (
        infinita
          ? <b className={`bb-q bb-q--vip${semPrata ? ' is-falta' : ''}`}>∞</b>
          : <b className={`bb-q${qty <= 0 ? ' is-falta' : ''}`}>{numero(qty)}</b>
      )}
      {esperando && esperaAte != null && (
        <i
          key={esperaAte}
          className="bb-cd"
          aria-hidden="true"
          style={{ animationDuration: `${esperaMs}ms`, animationDelay: `-${Math.max(0, esperaMs - remaining)}ms` }}
        />
      )}
      {esperando && esperaTipo === 'recarga' && remaining >= 1000 && (
        <b className="bb-cdn" aria-hidden="true">{Math.ceil(remaining / 1000)}</b>
      )}
      {!armaSlot && occupied && (
        <span
          className="bb-ed"
          aria-hidden="true"
          title={t('magiaUi.trocarSlot', { tecla: keyCurta })}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); onOpen(); }}
        >
          ✎
        </span>
      )}
    </button>
    </>
  );
}

/** Slot da arma vazio: dois punhos cruzados (docs/prototipos/corrida-e-sem-arma-prototipo.html, B). */
function IcPunhos() {
  return (
    <svg className="bb-punho" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 19l7-7M20 19l-7-7" />
      <rect x="2.2" y="15.6" width="4.6" height="4" rx="1.4" transform="rotate(-45 4.5 17.6)" />
      <rect x="17.2" y="15.6" width="4.6" height="4" rx="1.4" transform="rotate(45 19.5 17.6)" />
      <circle cx="12" cy="9" r="4.2" />
      <path d="M10.4 8.2h3.2M10.4 10h3.2" />
    </svg>
  );
}
